/**
 * Coverage and liquidity — `/v1/admin/supply/coverage` (E36).
 *
 * **The only aggregate over skills.** Every other skills route is per-pandit, and no number of
 * per-entity endpoints answers "which pujas are thin in which cities" — which is why this screen
 * sat on fixtures until the read existed.
 *
 * **The whole grid arrives in one response and that is deliberate.** It is a matrix; paging a
 * matrix hides the cell somebody needed to see. The server bounds it by construction — launched
 * cities times active pujas — so there is nothing to page.
 */
import { useQuery } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type CoverageGrid = components['schemas']['CoverageGridOut'];
export type CoverageAxis = components['schemas']['CoverageAxisOut'];
export type CoverageCell = components['schemas']['CoverageCellOut'];
export type SupplyLiquidity = components['schemas']['SupplyLiquidityOut'];

export function useCoverageGrid() {
  return useQuery({
    queryKey: keys.supply.coverage(),
    queryFn: () => authedClient.request<CoverageGrid>('/v1/admin/supply/coverage'),
  });
}

/** Each launched city's dispatchable roster beside its open demand, next 7 days. */
export function useSupplyLiquidity(enabled = true) {
  return useQuery({
    queryKey: keys.supply.liquidity(),
    queryFn: () => authedClient.request<SupplyLiquidity>('/v1/admin/supply/liquidity'),
    enabled,
  });
}

/** `puja_id:city_id` → count, so a cell lookup is O(1) rather than a scan per intersection. */
export function cellIndex(grid: CoverageGrid): Map<string, number> {
  return new Map(grid.cells.map((cell) => [`${cell.puja_id}:${cell.city_id}`, cell.pandit_count]));
}
